import { useCallback, useEffect, useState } from 'react'
import styles from './PromptElement.module.scss'
import { ElementWrapper } from '../components/ElementWrapper/ElementWrapper'
import { useDispatch, useSelector } from 'react-redux'
import {
    deletePrompt,
    updatePromptPosition,
    updatePromptProps,
    updatePromptSize,
} from '../../../store/actions'
import { DotsThreeIcon, PencilSimpleIcon, TrashIcon } from '@phosphor-icons/react'
import {
    setLastEditedPrompt,
    setPromptSelecting,
    setPromptSelectingObjects,
} from '../../../store/actions/promptActions'
import { useModal, MODALS } from '../../modals'
import {
    ScoopSelector,
    ScoopText,
    ScoopButton,
    ScoopMenu,
    ScoopMenuItem,
    ScoopDatePicker,
    ScoopTextField,
    useToast,
} from '../../ui'
import { useApi } from '../../../api/api'
import { debounce } from 'lodash'
import { NumericRange } from './components/NumericRange/NumericRange'

export const PromptElement = ({
    id,
    initialSize,
    initialPosition,
    promptProps,
    workspaceMetadata,
    multiProps,
}) => {
    const promptId = `PromptElement-${id}`
    const dispatch = useDispatch()
    const { postData } = useApi()
    const toast = useToast()
    const [menuAnchorEl, setMenuAnchorEl] = useState(null)
    const activeMode = useSelector((state) => state.ui.activeMode)
    const isGuestMode = useSelector((state) => state.auth.isGuestMode)
    const guestPrompts = useSelector((state) => state.auth.guestPrompts)
    const [isEditMode, setIsEditMode] = useState(false)
    const deleteModal = useModal(MODALS.COMMON.DELETE)
    const [categoryValues, setCategoryValues] = useState([])
    const [categoryValuesLoading, setCategoryValuesLoading] = useState(false)
    const [singleSelectValue, setSingleSelectValue] = useState('')
    const [multipleSelectValue, setMultipleSelectValue] = useState([])
    const [dateValue, setDateValue] = useState(null)
    const [numericRange, setNumericRange] = useState(null)
    const [singleValue, setSingleValue] = useState(0)
    const selectDebounce = useCallback(
        debounce((value) => {
            const newPrompt = { ...promptProps }
            newPrompt.value = value
            if (promptProps.type === 'multi-select') {
                newPrompt.prompt.filterValue.values =
                    value.length === categoryValues.length ? ['All'] : value
            } else {
                newPrompt.prompt.filterValue.values[0] = value
            }
            dispatch(updatePromptProps(id, newPrompt))
            dispatch(setLastEditedPrompt(newPrompt))
        }, 1000),
        [promptProps]
    )
    const singleValueDebounce = useCallback(
        debounce((value) => {
            const newPrompt = { ...promptProps }
            newPrompt.value = value
            newPrompt.prompt.filterValue.values[0] = value
            dispatch(updatePromptProps(id, newPrompt))
            dispatch(setLastEditedPrompt(newPrompt))
        }, 1000),
        [promptProps]
    )

    const getTheme = () => {
        let th = undefined
        if (workspaceMetadata?.themes) {
            workspaceMetadata.themes.forEach((t) => {
                if (t.themeID === promptProps.theme) th = t
            })
        }
        return th
    }

    const theme = getTheme()

    const getCategoryValues = (tableID, worksheetID, rangeName, columnName, likeValue) => {
        setCategoryValuesLoading(true)
        const action = {
            action: 'getCategoryValues',
            columnName: columnName,
            like: likeValue,
        }
        if (tableID) {
            action.reportSeriesTableID = tableID
        } else if (worksheetID) {
            action.worksheetID = worksheetID
            action.rangeName = rangeName
        }
        postData(action)
            .then((r) => {
                setCategoryValuesLoading(false)
                setCategoryValues(r.values)
            })
            .catch(() => {
                setCategoryValuesLoading(false)
                setCategoryValues([])
                toast.error('Error fetching values, try again later.')
            })
    }

    useEffect(() => {
        if (
            !isGuestMode &&
            (promptProps.dataSourceId || (promptProps.worksheetId && promptProps.rangeName)) &&
            promptProps.fieldName &&
            !['data-range', 'single-date'].includes(promptProps.type)
        ) {
            getCategoryValues(
                promptProps.dataSourceId,
                promptProps.worksheetId,
                promptProps.rangeName,
                promptProps.fieldName
            )
        }
    }, [promptProps.fieldName, promptProps.dataSourceId, isGuestMode])

    useEffect(() => {
        let props
        if (isGuestMode && guestPrompts.length > 0) {
            const guestPrompt = guestPrompts.find((p) => p.id === id)
            props = guestPrompt?.promptProps ?? promptProps
        } else {
            props = promptProps
        }
        if (!props) return
        switch (props.type) {
            case 'single-select':
                setSingleSelectValue(props.value || '')
                break
            case 'multi-select':
                setMultipleSelectValue(props.value || [])
                break
            case 'single-date':
                setDateValue(props.value ? new Date(props.value) : null)
                break
            case 'date-range':
                setDateValue(
                    props.value ? [new Date(props.value[0]), new Date(props.value[1])] : null
                )
                break
            case 'numeric-range':
                setNumericRange(props.value || null)
                break
            case 'single-value':
                setSingleValue(props.value || 0)
                break
        }
    }, [promptProps, isGuestMode, guestPrompts])

    const updateInsightPos = (position) => {
        dispatch(updatePromptPosition(id, position))
    }

    const updateInsightSz = (size) => {
        dispatch(updatePromptSize(id, size))
    }

    const handleDeletePrompt = () => {
        dispatch(deletePrompt(id))
        dispatch(setLastEditedPrompt(promptProps))
    }

    const handleEdit = () => {
        dispatch(setPromptSelecting(true, promptId))
        dispatch(setPromptSelectingObjects(promptProps.objects))
        setMenuAnchorEl(null)
    }

    const handleCloseMenu = () => {
        setMenuAnchorEl(null)
    }

    const createMenuItemHandler = (handler) => (event) => {
        event.stopPropagation()
        handler()
        setMenuAnchorEl(null)
    }

    const handleMultiSelectChange = (newValues) => {
        setMultipleSelectValue(newValues)
        selectDebounce(newValues)
    }

    const handleDateChange = (value) => {
        setDateValue(value)
        const newPrompt = { ...promptProps }
        if (newPrompt.type === 'date-range') {
            newPrompt.value = [value[0].toString(), value[1].toString()]
            newPrompt.prompt[0].filterValue.values[0] = value[0].toISOString().split('T')[0]
            newPrompt.prompt[1].filterValue.values[0] = value[1].toISOString().split('T')[0]
        } else {
            newPrompt.value = value.toString()
            newPrompt.prompt.filterValue.values[0] = value.toISOString().split('T')[0]
        }
        dispatch(updatePromptProps(id, newPrompt))
        dispatch(setLastEditedPrompt(newPrompt))
    }

    const handleNumericChange = (value) => {
        setNumericRange(value)
        const newPrompt = { ...promptProps }
        newPrompt.value = value
        newPrompt.prompt[0].filterValue.values[0] = value[0]
        newPrompt.prompt[1].filterValue.values[0] = value[1]
        dispatch(updatePromptProps(id, newPrompt))
        dispatch(setLastEditedPrompt(newPrompt))
    }

    const handleSingleValueChange = (value) => {
        setSingleValue(value)
        singleValueDebounce(value)
    }

    const handleSelectChange = (value) => {
        setSingleSelectValue(value)
        selectDebounce(value)
    }

    const getPromptContent = () => {
        if (
            (promptProps.dataSourceId || (promptProps.worksheetId && promptProps.rangeName)) &&
            promptProps.fieldName &&
            promptProps.type
        ) {
            switch (promptProps.type) {
                case 'single-select':
                    return (
                        <div className={styles.contentWrapper}>
                            {categoryValues.length > 0 && (
                                <ScoopSelector
                                    value={singleSelectValue || ''}
                                    onChange={(e) => handleSelectChange(e.target.value)}
                                    options={categoryValues.map((v) => ({ label: v, value: v }))}
                                    className={styles.selector}
                                    disabled={isGuestMode}
                                    size="sm"
                                    disablePortal
                                    style={{
                                        backgroundColor: theme?.colorScheme?.backgroundColor || '',
                                        color: theme?.colorScheme?.darkTheme ? 'white' : '',
                                        border: theme?.colorScheme?.darkTheme
                                            ? '1px solid white'
                                            : '',
                                    }}
                                    menuStyle={{
                                        backgroundColor: theme?.colorScheme?.backgroundColor || '',
                                        '--selector-border-color': theme?.colorScheme?.darkTheme
                                            ? 'rgba(255, 255, 255, 0.3)'
                                            : '',
                                    }}
                                    menuItemStyle={{
                                        color: theme?.colorScheme?.darkTheme ? 'white' : '',
                                        '--menu-item-hover-bg': theme?.colorScheme?.darkTheme
                                            ? 'rgba(255, 255, 255, 0.1)'
                                            : '',
                                        '--menu-item-selected-bg': theme?.colorScheme?.darkTheme
                                            ? 'transparent'
                                            : '',
                                    }}
                                />
                            )}
                        </div>
                    )
                case 'multi-select':
                    return (
                        <div className={styles.contentWrapper}>
                            <ScoopSelector
                                value={multipleSelectValue}
                                onChange={(e) => handleMultiSelectChange(e.target.value)}
                                options={categoryValues.map((v) => ({ label: v, value: v }))}
                                placeholder="Select values..."
                                className={styles.selector}
                                disabled={isGuestMode}
                                size="sm"
                                multiple
                                showChips
                                chipsDeletable={!isGuestMode}
                                chipsInline
                                maxChipsVisible="auto"
                                searchable={categoryValues.length > 10}
                                showSelectAll
                                disablePortal
                                style={{
                                    backgroundColor: theme?.colorScheme?.backgroundColor || '',
                                    color: theme?.colorScheme?.darkTheme ? 'white' : '',
                                    border: theme?.colorScheme?.darkTheme ? '1px solid white' : '',
                                }}
                                menuStyle={{
                                    backgroundColor: theme?.colorScheme?.backgroundColor || '',
                                    '--selector-border-color': theme?.colorScheme?.darkTheme
                                        ? 'rgba(255, 255, 255, 0.3)'
                                        : '',
                                }}
                                menuItemStyle={{
                                    color: theme?.colorScheme?.darkTheme ? 'white' : '',
                                    '--menu-item-hover-bg': theme?.colorScheme?.darkTheme
                                        ? 'rgba(255, 255, 255, 0.1)'
                                        : '',
                                    '--menu-item-selected-bg': theme?.colorScheme?.darkTheme
                                        ? 'transparent'
                                        : '',
                                }}
                                searchStyle={{
                                    backgroundColor: theme?.colorScheme?.backgroundColor || '',
                                    color: theme?.colorScheme?.darkTheme ? 'white' : '',
                                    border: theme?.colorScheme?.darkTheme
                                        ? '1px solid rgba(255, 255, 255, 0.3)'
                                        : '',
                                }}
                            />
                        </div>
                    )
                case 'single-date':
                case 'date-range':
                    return (
                        <ScoopDatePicker
                            value={dateValue}
                            onChange={handleDateChange}
                            range={promptProps.type === 'date-range'}
                            isGuestMode={isGuestMode}
                            theme={theme}
                        />
                    )
                case 'numeric-range':
                    return (
                        <NumericRange
                            value={numericRange}
                            onChange={handleNumericChange}
                            max={parseInt(categoryValues[categoryValues.length - 1])}
                            min={categoryValues[0] !== null ? parseInt(categoryValues[0]) : 0}
                            isGuestMode={isGuestMode}
                            theme={theme}
                        />
                    )
                case 'single-value':
                    return (
                        <ScoopTextField
                            className={styles.singleValueInput}
                            value={singleValue}
                            onChange={(e) =>
                                handleSingleValueChange(e.target.value.replace(/\D/g, ''))
                            }
                            type="number"
                            style={{
                                color: theme?.colorScheme?.darkTheme ? 'white' : '',
                                border: theme?.colorScheme?.darkTheme ? '1px solid white' : '',
                            }}
                        />
                    )
                default:
                    return null
            }
        } else {
            return (
                <ScoopButton
                    className={styles.configButton}
                    onClick={handleEdit}
                    disabled={isGuestMode}
                >
                    Configure
                </ScoopButton>
            )
        }
    }

    return (
        <ElementWrapper
            type={'Prompt'}
            id={promptId}
            key={promptId}
            editMode={isEditMode}
            toggleEditMode={setIsEditMode}
            initialPosition={initialPosition}
            initialSize={initialSize}
            updateElementPosition={updateInsightPos}
            updateElementSize={updateInsightSz}
            minSize={{ width: 300, height: 100 }}
            backgroundColor={theme ? theme.colorScheme.backgroundColor : undefined}
            withTheme={!!theme}
            {...multiProps}
            objectId={id}
        >
            <div className={styles.promptContainer}>
                <div className={styles.promptHeader}>
                    <ScoopText
                        className={styles.promptTitle}
                        style={{ color: theme?.colorScheme?.darkTheme ? 'white' : 'black' }}
                    >
                        {promptProps.label || 'Untitled prompt'}
                    </ScoopText>
                    {activeMode === 'edit' && (
                        <ScoopButton
                            size="xxs"
                            variant="text"
                            className={styles.menuButton}
                            onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                        >
                            <DotsThreeIcon size={20} weight="regular" />
                        </ScoopButton>
                    )}
                </div>
                {getPromptContent()}
            </div>
            <ScoopMenu
                open={Boolean(menuAnchorEl)}
                anchorEl={menuAnchorEl}
                onClose={handleCloseMenu}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
            >
                <ScoopMenuItem
                    leftSection={<PencilSimpleIcon size={18} weight="regular" />}
                    primary="Edit"
                    onClick={createMenuItemHandler(handleEdit)}
                />
                <ScoopMenuItem
                    leftSection={<TrashIcon size={18} weight="regular" />}
                    primary="Delete"
                    onClick={createMenuItemHandler(() =>
                        deleteModal.show({
                            title: promptProps.label || 'Prompt',
                            type: 'Prompt',
                            onDelete: handleDeletePrompt,
                        })
                    )}
                />
            </ScoopMenu>
        </ElementWrapper>
    )
}
